<template>
    <RaterChrome :show-anonymous="!invite?.is_authenticated">
        <div v-if="loading" class="body-small text-valence-stone-500">Loading…</div>
        <BaseNotification v-else-if="loadError" theme="error" :text="loadError" hide-dismiss />
        <template v-else-if="subjectFirstName && responseId">
            <div class="relationship-page">
                <a :href="`/reflections/360/r/${props.invite_token}/`" class="back-link">
                    <i class="bi bi-chevron-left" aria-hidden="true"></i>
                    Back
                </a>

                <header class="header">
                    <h1 class="display-serif text-valence-stone-900 leading-tight">Your relationship to {{ subjectFirstName }}</h1>
                    <p class="body-small text-valence-stone-500">Pick one. Your selection helps us aggregate honestly — it doesn't identify you to {{ subjectFirstName }}.</p>
                </header>

                <hr class="divider" />

                <div class="options" role="radiogroup" aria-label="Your relationship">
                    <button
                        v-for="opt in options"
                        :key="opt.id"
                        type="button"
                        role="radio"
                        :aria-checked="selected === opt.id"
                        class="option"
                        :class="{ 'option--selected': selected === opt.id }"
                        @click="selected = opt.id">
                        <div class="option__label">{{ opt.label }}</div>
                        <div class="option__body">{{ opt.body }}</div>
                    </button>
                </div>

                <BaseNotification v-if="error" theme="error" :text="error" hide-dismiss />

                <div class="actions">
                    <a :href="`/reflections/360/r/${props.invite_token}/`" class="back-text">Back</a>
                    <button type="button" class="base-btn-dark base-btn-square inline-flex items-center gap-2" :disabled="!selected || submitting" @click="onContinue">
                        Continue <i class="bi bi-arrow-right" aria-hidden="true"></i>
                    </button>
                </div>
            </div>
        </template>
    </RaterChrome>
</template>

<script setup lang="ts">
import { router } from "@inertiajs/vue3";
import { useFetch } from "~vue/api";
import BaseNotification from "~vue/components/BaseNotification.vue";
import RaterChrome from "~vue/components/RaterChrome.vue";
import { useInviteLoader } from "~vue/composables/useInviteLoader";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { markPagePublic } from "~vue/utils/sessionEnded";
import { DEFAULT_RATER_RELATIONSHIPS, isRelationshipRejected, relationshipOptionBody } from "~vue/utils/threeSixty";
import { computed, onMounted, ref, watch } from "vue";

interface ResponsePayload {
    id: string;
    relationship: string | null;
}

interface Props {
    invite_token: string;
    is_anonymous: boolean;
}

const props = defineProps<Props>();

// An external rater has no account to log back into, so a rejected token there must stay silent.
// An internal rater is signed in, and a real session expiry must still offer them recovery.
if (props.is_anonymous) {
    markPagePublic();
}

const {
    invite,
    loading: inviteLoading,
    loadError: inviteLoadError,
    reload: reloadInvite,
} = useInviteLoader(props.invite_token, {
    redirectIfResponded: `/reflections/360/r/${props.invite_token}/done`,
    redirectIfCycleClosed: `/reflections/360/r/${props.invite_token}/`,
});

// The subject already categorized this rater at nomination — the relationship
// step doesn't apply, so a direct visit skips to the questions.
watch(invite, (loaded) => {
    if (loaded?.relationship_prefilled) {
        router.visit(`/reflections/360/r/${props.invite_token}/q/1`);
    }
});

const responseId = ref<string>("");
const selected = ref<string | null>(null);
const responseLoading = ref(true);
const responseLoadError = ref<string | null>(null);
const submitting = ref(false);
const error = ref<string | null>(null);

const subjectFirstName = computed(() => invite.value?.subject_first_name ?? "");
const loading = computed(() => inviteLoading.value || responseLoading.value);
const loadError = computed(() => inviteLoadError.value || responseLoadError.value);

const options = computed(() =>
    (invite.value?.relationships ?? DEFAULT_RATER_RELATIONSHIPS).map((option) => ({
        id: option.value,
        label: option.label,
        body: relationshipOptionBody(option.value, subjectFirstName.value),
    })),
);

onMounted(async () => {
    const { data, error: fetchError } = await useFetch(`/v1/reflections/invites/${props.invite_token}/response`).post().json();
    responseLoading.value = false;
    if (fetchError.value) {
        responseLoadError.value = (data.value as { error?: string })?.error || "Could not load invite.";
        return;
    }
    const response = (data.value as { response: ResponsePayload }).response;
    responseId.value = response.id;
    if (response.relationship && response.relationship !== "self") {
        selected.value = response.relationship;
    }
    void logUserInteraction(AmplitudeEventNames.ASSESSMENT_RATER_RELATIONSHIP_VIEWED, {
        has_existing_selection: response.relationship !== null,
    });
});

async function onContinue() {
    if (!selected.value || submitting.value) {
        return;
    }
    submitting.value = true;
    error.value = null;
    try {
        const { error: fetchError, data } = await useFetch(`/v1/reflections/responses/${responseId.value}/relationship?invite_token=${props.invite_token}`)
            .post(JSON.stringify({ relationship: selected.value }), "application/json")
            .json();
        if (fetchError.value) {
            error.value = (data.value as { error?: string })?.error || "Could not save.";
            // The organization dropped this category while the page was open. Re-read
            // the current ones and drop the now-invalid pick, so the rater can carry on
            // instead of every retry failing against a list they can't see.
            if (isRelationshipRejected(data.value)) {
                await reloadInvite();
                if (!options.value.some((opt) => opt.id === selected.value)) {
                    selected.value = null;
                }
            }
            return;
        }
        void logUserInteraction(AmplitudeEventNames.ASSESSMENT_RATER_RELATIONSHIP_SUBMITTED, {
            relationship: selected.value,
        });
        router.visit(`/reflections/360/r/${props.invite_token}/q/1`);
    } finally {
        submitting.value = false;
    }
}
</script>

<style scoped>
@reference "../../../css/tailwind.css";

.relationship-page {
    @apply flex flex-col gap-6;
}

.back-link {
    @apply inline-flex items-center gap-1 self-start body-small text-valence-stone-500 hover:text-valence-green-olive transition-colors;
}

.header {
    @apply flex flex-col gap-3;
}

.divider {
    @apply border-t border-valence-cream-300;
}

.options {
    @apply flex flex-col gap-3;
}

.option {
    @apply w-full text-left rounded-lg border border-valence-cream-300 bg-white px-5 py-4 transition-colors;
    @apply hover:bg-valence-cream-50;
}

.option:focus-visible {
    @apply outline-none ring-2 ring-valence-green-olive;
}

.option--selected {
    @apply bg-valence-green-olive-light border-valence-green-olive;
}

.option--selected:hover {
    @apply bg-valence-green-olive-light;
}

.option__label {
    @apply body-bold text-valence-stone-900;
}

.option__body {
    @apply body-small text-valence-stone-500 mt-1;
}

.option--selected .option__body {
    @apply text-valence-stone-700;
}

.actions {
    @apply flex items-center justify-between pt-2;
}

.back-text {
    @apply body-small text-valence-stone-500 hover:text-valence-stone-900 transition-colors;
}
</style>
